import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AUTH_TOKEN } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';

export const initialState = {
	loading: true,  // Start with loading true to prevent premature routing
	message: '',
	showMessage: false,
	redirect: '',
	token: null,  // Let initializeAuth properly set this from localStorage
	user: null    // Store user information from Planning Center
}

export const signIn = createAsyncThunk('auth/login',async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		// Accept any email and password for authentication
		if (email && password) {
			// Generate a mock token for any valid credentials
			const mockToken = 'mock-auth-token-' + Date.now();
			localStorage.setItem(AUTH_TOKEN, mockToken);
			
			// Store mock user data
			const mockUser = {
				id: 'mock-user-' + Date.now(),
				name: email.split('@')[0], // Use email prefix as name
				email: email
			};
			localStorage.setItem('user', JSON.stringify(mockUser));
			
			return { token: mockToken, user: mockUser };
		} else {
			return rejectWithValue('Email and password are required')
		}
	} catch (err) {
		return rejectWithValue('Authentication failed')
	}
})

export const signUp = createAsyncThunk('auth/register',async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.register({email, password})
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signOut = createAsyncThunk('auth/logout',async () => {
	// Clear all authentication tokens
	localStorage.removeItem(AUTH_TOKEN);
	localStorage.removeItem('pco_session_token');
	localStorage.removeItem('pco_refresh_token');
	localStorage.removeItem('user');
	
	// For Planning Center OAuth, we just clear local storage
	// No need to call external service
	return { success: true }
})

export const signInWithGoogle = createAsyncThunk('auth/signInWithGoogle', async (_, { rejectWithValue }) => {
    try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const signInWithFacebook = createAsyncThunk('auth/signInWithFacebook', async (_, { rejectWithValue }) => {
    try {
		const response = await AuthService.loginInOAuth()
		const token = response.data.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return token;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload.token || action.payload
			// Store user information if provided
			if (action.payload.user) {
				state.user = action.payload.user
			}
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.user = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		},
		initializeAuth: (state) => {
			// Force check localStorage and update token
			const token = localStorage.getItem(AUTH_TOKEN);
			const pcoToken = localStorage.getItem('pco_session_token');
			const userStr = localStorage.getItem('user');
			
			console.log('Initializing auth:', { token, pcoToken, user: userStr });
			
			// Check if we have a valid token (either mock or PCO)
			if (token && (token.startsWith('mock-auth-token-') || pcoToken)) {
				state.token = token;
				// Restore user information if available
				if (userStr) {
					try {
						state.user = JSON.parse(userStr);
					} catch (e) {
						console.error('Error parsing user data:', e);
						state.user = null;
					}
				}
				console.log('Valid token found, user authenticated');
			} else {
				// Clear invalid tokens
				localStorage.removeItem(AUTH_TOKEN);
				localStorage.removeItem('pco_session_token');
				localStorage.removeItem('pco_refresh_token');
				localStorage.removeItem('user');
				state.token = null;
				state.user = null;
				console.log('No valid token found, user not authenticated');
			}
			state.loading = false;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				// Handle both old format (just token) and new format (token + user)
				if (action.payload.token) {
					state.token = action.payload.token
					state.user = action.payload.user
				} else {
					state.token = action.payload
				}
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.pending, (state) => {
				state.loading = true
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.user = null
				state.redirect = '/auth/login'
			})
			.addCase(signOut.rejected, (state, action) => {
				state.loading = false
				state.token = null
				state.user = null
				state.redirect = '/auth/login'
			})
			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithGoogle.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithGoogle.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithGoogle.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signInWithFacebook.pending, (state) => {
				state.loading = true
			})
			.addCase(signInWithFacebook.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload
			})
			.addCase(signInWithFacebook.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
	},
})

export const { 
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	initializeAuth
} = authSlice.actions

export default authSlice.reducer